import { getSupabaseClient } from '../../../../core/supabase/client.js';
import type { PayoutRowDto, PayoutStatus } from '../dtos/PayoutRequestDto.js';
import { UploadPath } from '../../../upload/upload_api.js';

const FIELDS = `
  id, session_id, location_id, formx_submission_id,
  status, created_by, rejected_by_role,
  org_id, project_id, created_at,
  payment_receipt_url, reject_reason, close_reason,
  photo_fields, extracted_names, match_confidences, matched_contact_ids,
  contact_id,
  owner:contacts!contact_id(id, full_name, id_number, bank_accounts),
  location:locations!location_id(
    id, name, code, address_line, province_code,
    province:locations_provinces!province_code(name)
  ),
  creator:profiles!created_by(id, first_name, last_name, email)
`;

function buildFields(options: PayoutListOptionsDs): string {
  const isLocationFilter = !!options.locationName;
  const isProvinceFilter = !!options.provinceName;

  const locationJoin = isLocationFilter || isProvinceFilter ? 'locations!location_id!inner' : 'locations!location_id';
  const provinceJoin = isProvinceFilter ? 'locations_provinces!province_code!inner' : 'locations_provinces!province_code';

  return `
    id, session_id, location_id, formx_submission_id,
    status, created_by, rejected_by_role,
    org_id, project_id, created_at,
    payment_receipt_url, reject_reason, close_reason,
    photo_fields, extracted_names, match_confidences, matched_contact_ids,
    contact_id,
    owner:contacts!contact_id(id, full_name, id_number, bank_accounts),
    location:${locationJoin}(
      id, name, code, address_line, province_code,
      province:${provinceJoin}(name)
    ),
    creator:profiles!created_by(id, first_name, last_name, email),
    submitter_name,
    validator_name,
    payer_name
  `;
}

function buildRawFields(options: Pick<PayoutListOptionsDs, 'locationName' | 'provinceName'>): string {
  const isLocationFilter = !!options.locationName;
  const isProvinceFilter = !!options.provinceName;
  const locationJoin = isLocationFilter || isProvinceFilter ? 'locations!location_id!inner' : 'locations!location_id';
  const provinceJoin = isProvinceFilter ? 'locations_provinces!province_code!inner' : 'locations_provinces!province_code';
  return `
    id, session_id, location_id, formx_submission_id,
    status, created_by, rejected_by_role,
    org_id, project_id, created_at,
    payment_receipt_url, reject_reason, close_reason,
    photo_fields, extracted_names, match_confidences, matched_contact_ids,
    contact_id,
    owner:contacts!contact_id(id, full_name, id_number, bank_accounts),
    location:${locationJoin}(
      id, name, code, address_line, province_code,
      province:${provinceJoin}(name)
    ),
    creator:profiles!created_by(id, first_name, last_name, email)
  `;
}

export interface PayoutListOptionsDs {
  orgId: string;
  projectId?: string;
  statusIn?: PayoutStatus[];
  sessionId?: string;
  limit?: number;
  offset?: number;
  locationName?: string;
  provinceName?: string;
  submitterName?: string;
  validatorName?: string;
  payerName?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  sortBy?: 'created_at' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export class PayoutRemoteDataSource {
  private get supabase() { return getSupabaseClient() as any; }

  /** Latest row per session — drives inbox queries. Backed by payouts_latest view. */
  async list(options: PayoutListOptionsDs): Promise<{ data: PayoutRowDto[]; total: number }> {
    const fields = buildFields(options);
    let query = this.supabase.from('payouts_latest').select(fields, { count: 'exact' })
      .eq('org_id', options.orgId);
    if (options.projectId) query = query.eq('project_id', options.projectId);
    if (options.statusIn?.length) query = query.in('status', options.statusIn);
    if (options.sessionId) query = query.eq('session_id', options.sessionId);

    if (options.locationName) {
      query = query.ilike('location.name', `%${options.locationName}%`);
    }
    if (options.provinceName) {
      query = query.ilike('location.province.name', `%${options.provinceName}%`);
    }
    if (options.submitterName) {
      query = query.ilike('submitter_name', `%${options.submitterName}%`);
    }
    if (options.validatorName) {
      query = query.ilike('validator_name', `%${options.validatorName}%`);
    }
    if (options.payerName) {
      query = query.ilike('payer_name', `%${options.payerName}%`);
    }
    if (options.createdAtFrom) {
      query = query.gte('created_at', options.createdAtFrom);
    }
    if (options.createdAtTo) {
      query = query.lte('created_at', options.createdAtTo);
    }

    if (options.sortBy) {
      const ascending = options.sortOrder === 'asc';
      query = query.order(options.sortBy, { ascending });
      if (options.sortBy === 'status') {
        query = query.order('created_at', { ascending: false });
      }
    } else {
      query = query.order('created_at', { ascending: false });
    }

    if (typeof options.limit === 'number' && typeof options.offset === 'number') {
      query = query.range(options.offset, options.offset + options.limit - 1);
    } else if (typeof options.limit === 'number') {
      query = query.limit(options.limit);
    }
    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { data: data ?? [], total: count ?? 0 };
  }

  /** Every row in payouts (no DISTINCT) — for the superadmin all-records view. */
  async listRaw(options: PayoutListOptionsDs): Promise<{ data: PayoutRowDto[]; total: number }> {
    const fields = buildRawFields(options);
    let query = this.supabase.from('payouts').select(fields, { count: 'exact' })
      .eq('org_id', options.orgId);
    if (options.projectId) query = query.eq('project_id', options.projectId);
    if (options.statusIn?.length) query = query.in('status', options.statusIn);
    if (options.sessionId) query = query.eq('session_id', options.sessionId);
    if (options.locationName) query = query.ilike('location.name', `%${options.locationName}%`);
    if (options.provinceName) query = query.ilike('location.province.name', `%${options.provinceName}%`);
    if (options.createdAtFrom) query = query.gte('created_at', options.createdAtFrom);
    if (options.createdAtTo) query = query.lte('created_at', options.createdAtTo);
    if (options.sortBy) {
      query = query.order(options.sortBy, { ascending: options.sortOrder === 'asc' });
    } else {
      query = query.order('created_at', { ascending: false });
    }
    if (typeof options.limit === 'number' && typeof options.offset === 'number') {
      query = query.range(options.offset, options.offset + options.limit - 1);
    } else if (typeof options.limit === 'number') {
      query = query.limit(options.limit);
    }
    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { data: data ?? [], total: count ?? 0 };
  }

  /** Full chain for a session, oldest first. */
  async getChainBySessionId(sessionId: string): Promise<PayoutRowDto[]> {
    const { data, error } = await this.supabase.from('payouts')
      .select(FIELDS)
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  /** Full chains for multiple sessions, oldest first. */
  async getChainsBySessionIds(sessionIds: string[]): Promise<PayoutRowDto[]> {
    const { data, error } = await this.supabase.from('payouts')
      .select(FIELDS)
      .in('session_id', sessionIds)
      .order('created_at', { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async submitEvent(sessionId: string, eventType: string, payload: Record<string, unknown>): Promise<PayoutRowDto | null> {
    const { data, error } = await this.supabase.rpc('payout_submit_event', {
      p_session_id: sessionId,
      p_event_type: eventType,
      p_payload: payload,
    });
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async closeRequest(sessionId: string, reason: string): Promise<PayoutRowDto | null> {
    const { data, error } = await this.supabase.rpc('payout_close_request', {
      p_session_id: sessionId,
      p_reason: reason,
    });
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async createRequest(sessionId: string, formxSubmissionId: string | null): Promise<PayoutRowDto | null> {
    const { data, error } = await this.supabase.rpc('payout_create_request', {
      p_session_id: sessionId,
      p_formx_submission_id: formxSubmissionId,
    });
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async adminCreate(input: { orgId: string; projectId: string; locationId: string }): Promise<PayoutRowDto | null> {
    const { data, error } = await this.supabase.rpc('payout_admin_create', {
      p_org_id: input.orgId,
      p_project_id: input.projectId,
      p_location_id: input.locationId,
    });
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async adminDeleteBySession(sessionId: string): Promise<void> {
    const { error } = await this.supabase.rpc('payout_admin_delete', { p_session_id: sessionId });
    if (error) throw new Error(error.message);
  }

  /** Server-resolved canonical payout folder (single source of truth). */
  private async mediaFolder(sessionId: string, locationId: string): Promise<string> {
    const { data, error } = await this.supabase.rpc('get_media_folder', {
      p_mode: 'payout',
      p_session_id: sessionId,
      p_field_id: null,
      p_location_id: locationId,
    });
    if (error) throw new Error(error.message);
    if (!data) throw new Error('Failed to resolve payout media folder');
    return data as string;
  }

  async uploadReceipt(args: {
    sessionId: string;
    locationId: string;
    file: File;
  }): Promise<string> {
    const folder = await this.mediaFolder(args.sessionId, args.locationId);
    const path = UploadPath.buildFilePath(folder, args.file.name, args.sessionId);

    const { error } = await this.supabase.storage.from('media').upload(path, args.file, {
      cacheControl: '3600',
      upsert: false,
      contentType: args.file.type || undefined,
    });
    if (error) throw new Error(error.message);
    const { data } = await this.supabase.storage.from('media').createSignedUrl(path, 60 * 60 * 24 * 365 * 2);
    if (!data?.signedUrl) throw new Error('Failed to create signed URL for receipt');
    return data.signedUrl;
  }

  /**
   * Mint a single-use signed upload URL for a deterministic receipt path.
   * The accountant (authenticated, on PC) calls this; the token lets an
   * unauthenticated phone PUT a file to exactly this path — no login on phone.
   */
  async createReceiptUploadTarget(args: {
    sessionId: string;
    locationId: string;
  }): Promise<{ bucket: string; path: string; token: string }> {
    const folder = await this.mediaFolder(args.sessionId, args.locationId);
    const path = UploadPath.buildFilePath(folder, 'receipt.jpg', args.sessionId);
    const { data, error } = await this.supabase.storage.from('media').createSignedUploadUrl(path);
    if (error) throw new Error(error.message);
    return { bucket: 'media', path: data.path ?? path, token: data.token };
  }

  /** Upload from the phone using the signed-url token (no auth needed). */
  async uploadReceiptToSignedUrl(args: { bucket: string; path: string; token: string; file: File }): Promise<void> {
    const { error } = await this.supabase.storage.from(args.bucket)
      .uploadToSignedUrl(args.path, args.token, args.file, {
        contentType: args.file.type || undefined,
      });
    if (error) throw new Error(error.message);
  }

  /** Returns a long-lived signed download URL if the file exists at path, else null. */
  async getReceiptUrlIfUploaded(bucket: string, path: string): Promise<string | null> {
    const { data, error } = await this.supabase.storage.from(bucket)
      .createSignedUrl(path, 60 * 60 * 24 * 365 * 2);
    if (error || !data?.signedUrl) return null;
    return data.signedUrl;
  }

  subscribe(filter: { orgId?: string; projectId?: string; sessionId?: string }, onChange: (row: PayoutRowDto) => void) {
    const supa = this.supabase;
    const channel = supa.channel(`payouts_${filter.sessionId ?? filter.projectId ?? filter.orgId ?? 'all'}`);
    const f = filter.sessionId
      ? `session_id=eq.${filter.sessionId}`
      : filter.projectId
        ? `project_id=eq.${filter.projectId}`
        : filter.orgId
          ? `org_id=eq.${filter.orgId}`
          : undefined;
    // Append-only: only INSERTs change state.
    channel.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'payouts', filter: f }, (payload: any) => {
      onChange(payload.new ?? payload.old);
    });
    channel.subscribe();
    return () => { supa.removeChannel(channel); };
  }

  async invokeVerify(payoutId: string): Promise<void> {
    try {
      await this.supabase.functions.invoke('verify-payout-photos', { body: { request_id: payoutId } });
    } catch (err) {
      console.warn('[payout] verify invoke failed (non-blocking)', err);
    }
  }

  async canCloseSession(sessionId: string): Promise<boolean> {
    const { data, error } = await this.supabase.rpc('can_close_session', { p_session_id: sessionId });
    if (error) throw new Error(error.message);
    return !!data;
  }
}
